import { ref, type ComputedRef, type Ref } from 'vue'
import { type RouteLocationRaw } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'

import { materialsApi } from '@/api'
import {
  clearStoryboardSceneImage,
  generateConsistencyAssetImage,
  generateStoryboardSceneImage,
  generateStoryboardSceneVideo,
  getStoryboardSceneVideoTaskStatus,
  selectStoryboardSceneImage,
  updateStageContent,
  updateWorkflowInputData,
} from '@/api/workflow'
import type { ApprovalStage, WorkflowInstance } from '@/types/workflow'
import {
  consumeWorkflowKnowledgeConsistencyAssetImageSelections,
  consumeWorkflowKnowledgeContexts,
  consumeWorkflowKnowledgeImageSelections,
} from '@/utils/workflowKnowledge'

import { buildSceneImageCreativePrompt, buildScenePromptEngineeringContext, buildSceneVideoCreativePrompt } from './promptEngineering'
import { resolveSceneVideoDuration } from './sceneVideo'
import { useConsistencyAssetWorkspace } from './useConsistencyAssetWorkspace'
import { type SceneImageAction, useSceneImageWorkspace } from './useSceneImageWorkspace'
import { useSceneVideoWorkspace } from './useSceneVideoWorkspace'
import { useStoryboardDraftWorkspace } from './useStoryboardDraftWorkspace'
import { useStoryboardReferenceAssets } from './useStoryboardReferenceAssets'
import { useStoryboardSceneGeneration } from './useStoryboardSceneGeneration'
import { useStoryboardStageViewState } from './useStoryboardStageViewState'
import { useWorkspaceKnowledge } from './useWorkspaceKnowledge'
import type {
  ConsistencyAssetType,
  StoryboardWorkspaceState,
} from './types'

type StoryboardWorkspaceTab = 'script' | 'consistency' | 'generation'

interface UseStoryboardStageWorkspaceOptions {
  projectId: number
  currentStage: ComputedRef<ApprovalStage>
  workflow: Ref<WorkflowInstance | null>
  storyboardScenes: ComputedRef<any[]>
  storyboardWorkspaceState: ComputedRef<StoryboardWorkspaceState>
  uploadedSceneVideoMap: ComputedRef<Record<number, any>>
  generatedSceneVideoMap: ComputedRef<Record<number, any>>
  sceneVideoTaskMap: ComputedRef<Record<number, any>>
  sceneVideoFeedbackDrafts: Ref<Record<number, string>>
  generatedSceneVideoOverrides: Ref<Record<number, Record<string, any>>>
  workspaceBusyState: Ref<{ title: string; description: string; stageLabel: string; notice?: string } | null>
  editorContent: Ref<string>
  editorDirty: Ref<boolean>
  reviewFeedback: Ref<string>
  getStoryboardWorkspaceStatePayload: () => StoryboardWorkspaceState
  persistStoryboardWorkspaceState: (nextState: StoryboardWorkspaceState, options?: { successMessage?: string; silent?: boolean }) => Promise<void>
  getActionErrorMessage: (error: any) => string | null
  onRegenerateStoryboard: (payload: { feedback: string; editedContent?: string }) => Promise<void>
  routeFullPath: () => string
  routerPush: (location: RouteLocationRaw) => Promise<unknown>
  markEditorDirty: () => void
}

export function useStoryboardStageWorkspace(options: UseStoryboardStageWorkspaceOptions) {
  const storyboardWorkspaceTab = ref<StoryboardWorkspaceTab>('script')
  const storyboardGenerationTab = ref<'image' | 'video'>('image')
  const consistencyAssetTab = ref<ConsistencyAssetType>('character')
  const selectedStoryboardSceneNumber = ref<number | null>(null)
  const hoveredStoryboardSceneNumber = ref<number | null>(null)
  const sceneImageLoading = ref<Record<number, SceneImageAction | null>>({})
  const sceneImageInputs = ref<Record<number, string>>({})
  const sceneUploadInput = ref<HTMLInputElement | null>(null)
  const activeUploadSceneNumber = ref<number | null>(null)
  const sceneVideoUploadInput = ref<HTMLInputElement | null>(null)
  const activeSceneVideoNumber = ref<number | null>(null)
  const sceneVideoLoading = ref<Record<number, boolean>>({})
  const sceneVideoGenerating = ref<Record<number, boolean>>({})
  const sceneVideoSaving = ref<Record<number, boolean>>({})
  const trackedSceneVideoTaskIds = ref<Record<number, string>>({})
  const sceneVideoUploadDialogVisible = ref(false)
  const sceneVideoUploadFeedbackDraft = ref('')
  const pendingSceneVideoUploadNumber = ref<number | null>(null)
  const sceneVideoPlayerErrors = ref<Record<number, string>>({})

  const {
    applyStoryboardSceneUpdateFromServer,
    ensureStoryboardScenesReady,
    handleAddStoryboardScene,
    handleCancelStoryboardSceneEdit,
    handleDeleteStoryboardScene,
    handleFinishStoryboardSceneEdit,
    handleInsertStoryboardScene,
    handleSaveAndRegenerateStoryboard,
    handleSaveStoryboardScenes,
    handleToggleStoryboardSceneEdit,
    markStoryboardDraftDirty,
    storyboardDraftDirty,
    storyboardDraftScenes,
    storyboardEditingSceneNumber,
    storyboardSaving,
    syncStoryboardDraftScenes,
  } = useStoryboardDraftWorkspace({
    projectId: options.projectId,
    workflow: options.workflow,
    storyboardScenes: options.storyboardScenes,
    storyboardWorkspaceState: options.storyboardWorkspaceState,
    sceneImageInputs,
    sceneVideoFeedbackDrafts: options.sceneVideoFeedbackDrafts,
    editorContent: options.editorContent,
    editorDirty: options.editorDirty,
    selectedStoryboardSceneNumber,
    hoveredStoryboardSceneNumber,
    storyboardWorkspaceTab,
    reviewFeedback: options.reviewFeedback,
    getStoryboardWorkspaceStatePayload: options.getStoryboardWorkspaceStatePayload,
    persistStoryboardWorkspaceState: options.persistStoryboardWorkspaceState,
    updateStageContent,
    getActionErrorMessage: options.getActionErrorMessage,
    resolveDuration: resolveSceneVideoDuration,
    buildImagePrompt: (scene) => buildSceneImageCreativePrompt(buildScenePromptEngineeringContext({
      scene,
      consistencyAssets: consistencyAssets.value,
      consistencyAssetLookups: consistencyAssetLookups.value,
    })),
    buildVideoPrompt: (scene) => buildSceneVideoCreativePrompt(buildScenePromptEngineeringContext({
      scene,
      consistencyAssets: consistencyAssets.value,
      consistencyAssetLookups: consistencyAssetLookups.value,
    })),
    confirmDeleteScene: async (sceneNumber) => {
      try {
        await ElMessageBox.confirm(`确定删除镜头 ${sceneNumber} 吗？`, '删除分镜', {
          type: 'warning',
          confirmButtonText: '删除',
          cancelButtonText: '取消',
        })
        return true
      } catch {
        return false
      }
    },
    onRegenerateStoryboard: async (payload) => {
      await options.onRegenerateStoryboard(payload)
    },
    success: (message) => ElMessage.success(message),
    error: (message) => ElMessage.error(message),
    info: (message) => ElMessage.info(message),
  })

  const {
    activeConsistencyAssets,
    consistencyAssetImageGenerating,
    consistencyAssetImageUploading,
    consistencyAssetLookups,
    consistencyAssetSelectionOptions,
    consistencyAssetStats,
    consistencyAssetUploadInput,
    consistencyAssets,
    consistencyDirty,
    consistencySaving,
    handleAddConsistencyAsset,
    handleCreatePromptMentionAsset,
    handleClearConsistencyAssetImage,
    handleConsistencyAssetFileSelected,
    handleGenerateConsistencyAssetReference,
    handleRemoveConsistencyAsset,
    handleSaveConsistencyAssets,
    markConsistencyDirty,
    selectedStoryboardPromptMentionItems,
    syncConsistencyAssetsFromStoryboard,
    triggerConsistencyAssetUpload,
  } = useConsistencyAssetWorkspace({
    projectId: options.projectId,
    storyboardWorkspaceState: options.storyboardWorkspaceState,
    storyboardDraftScenes,
    consistencyAssetTab,
    workflow: options.workflow,
    workspaceBusyState: options.workspaceBusyState,
    persistStoryboardWorkspaceState: options.persistStoryboardWorkspaceState,
    getStoryboardWorkspaceStatePayload: options.getStoryboardWorkspaceStatePayload,
    uploadMaterial: (formData, uploadOptions) => materialsApi.upload(formData, uploadOptions),
    generateConsistencyAssetImageApi: generateConsistencyAssetImage,
    getActionErrorMessage: options.getActionErrorMessage,
    success: (message) => ElMessage.success(message),
    error: (message) => ElMessage.error(message),
  })

  const {
    clearSceneVideoPlaybackError,
    getSceneVideoTaskStatus,
    handleCancelSceneVideoUploadDialog,
    handleConfirmSceneVideoUploadDialog,
    handleSaveSceneVideoFeedback,
    handleSceneVideoFileSelected,
    handleSceneVideoLoadedData,
    handleSceneVideoPlaybackError,
    isSceneVideoGenerating,
    isSceneVideoTaskActive,
    refreshSceneVideoTaskStatus,
    stopAllSceneVideoTaskPolling,
    syncTaskPolling: syncSceneVideoTaskPolling,
    triggerSceneVideoUpload,
  } = useSceneVideoWorkspace({
    projectId: options.projectId,
    workflow: options.workflow,
    sceneVideoTaskMap: options.sceneVideoTaskMap,
    sceneVideoGenerating,
    sceneVideoLoading,
    sceneVideoSaving,
    trackedSceneVideoTaskIds,
    generatedSceneVideoOverrides: options.generatedSceneVideoOverrides,
    sceneVideoPlayerErrors,
    sceneVideoFeedbackDrafts: options.sceneVideoFeedbackDrafts,
    sceneVideoUploadDialogVisible,
    sceneVideoUploadFeedbackDraft,
    pendingSceneVideoUploadNumber,
    activeSceneVideoNumber,
    sceneVideoUploadInput,
    ensureStoryboardScenesReady,
    getStoryboardWorkspaceStatePayload: options.getStoryboardWorkspaceStatePayload,
    persistStoryboardWorkspaceState: options.persistStoryboardWorkspaceState,
    getSceneVideoTaskStatusApi: getStoryboardSceneVideoTaskStatus,
    uploadMaterial: (formData, uploadOptions) => materialsApi.upload(formData, uploadOptions),
    success: (message) => ElMessage.success(message),
    error: (message) => ElMessage.error(message),
    info: (message) => ElMessage.info(message),
    getActionErrorMessage: options.getActionErrorMessage,
  })

  const {
    applyQueuedKnowledgeConsistencyAssetImages,
    applyQueuedKnowledgeSceneImages,
    buildSceneImageOverlayState,
    formatSceneImageSource,
    handleClearSceneImage,
    handleGenerateSceneImage,
    handleSceneFileSelected,
    handleSelectSceneReference,
    isSceneImageBusy,
    isSceneImageLoading,
    triggerSceneFileUpload,
  } = useSceneImageWorkspace({
    projectId: options.projectId,
    workflow: options.workflow,
    storyboardScenes: options.storyboardScenes,
    consistencyAssets,
    consistencyDirty,
    sceneImageLoading,
    sceneImageInputs,
    sceneUploadInput,
    activeUploadSceneNumber,
    ensureStoryboardScenesReady,
    ensureConsistencyAssetsReady: async () => {
      if (!consistencyDirty.value) {
        return true
      }
      return handleSaveConsistencyAssets('一致性资产已保存，继续生成图片')
    },
    buildSceneGenerationRequest: (sceneNumber, purpose) => buildSceneGenerationRequest(sceneNumber, purpose),
    applyStoryboardSceneUpdateFromServer,
    handleSaveConsistencyAssets: async (successMessage) => {
      await handleSaveConsistencyAssets(successMessage)
    },
    generateSceneImageApi: generateStoryboardSceneImage,
    selectSceneImageApi: selectStoryboardSceneImage,
    clearSceneImageApi: clearStoryboardSceneImage,
    uploadMaterial: (formData, uploadOptions) => materialsApi.upload(formData, uploadOptions),
    consumeQueuedKnowledgeSceneImages: consumeWorkflowKnowledgeImageSelections,
    consumeQueuedKnowledgeConsistencyAssetImages: consumeWorkflowKnowledgeConsistencyAssetImageSelections,
    success: (message) => ElMessage.success(message),
    error: (message) => ElMessage.error(message),
    confirmClearSceneImage: async (sceneNumber) => {
      try {
        await ElMessageBox.confirm(`确定删除场景 ${sceneNumber} 当前使用的图片吗？`, '删除当前图片', {
          type: 'warning',
          confirmButtonText: '删除',
          cancelButtonText: '取消',
        })
        return true
      } catch {
        return false
      }
    },
  })

  const {
    selectedScenePreviewOverlayDescription,
    selectedScenePreviewOverlayNote,
    selectedScenePreviewOverlayTitle,
    selectedScenePreviewOverlayVisible,
    selectedSceneVideoTask,
    selectedSceneVideoTaskDescription,
    selectedSceneVideoTaskFacts,
    selectedSceneVideoTaskStatusLabel,
    selectedSceneVideoTaskTimeline,
    storyboardGenerationSceneItems,
    selectedStoryboardBoundAssets,
    selectedStoryboardImageUrl,
    selectedStoryboardSceneDraft,
    selectedStoryboardUploadedVideo,
    selectedStoryboardVideoError,
    selectedStoryboardVideoUrl,
    storyboardVisualActiveSceneNumber,
  } = useStoryboardStageViewState({
    storyboardDraftScenes,
    storyboardScenes: options.storyboardScenes,
    selectedStoryboardSceneNumber,
    hoveredStoryboardSceneNumber,
    consistencyAssets,
    uploadedSceneVideoMap: options.uploadedSceneVideoMap,
    generatedSceneVideoMap: options.generatedSceneVideoMap,
    sceneVideoTaskMap: options.sceneVideoTaskMap,
    sceneVideoPlayerErrors,
    storyboardGenerationTab,
    sceneImageLoading,
    sceneVideoLoading,
    sceneVideoGenerating,
    trackedSceneVideoTaskIds,
    getSceneVideoTaskStatus,
    isSceneVideoTaskActive,
    buildSceneImageOverlayState,
  })

  const {
    buildSceneKnowledgePrompt,
    handleOpenKnowledgeBaseForConsistencyAsset,
    handleOpenKnowledgeBaseForScene,
    hydrateKnowledgeContextsFromWorkflow,
    mergeKnowledgeContexts,
    persistKnowledgeContexts,
    selectedKnowledgeContexts,
  } = useWorkspaceKnowledge({
    projectId: options.projectId,
    routeFullPath: options.routeFullPath,
    currentStage: options.currentStage,
    workflow: options.workflow,
    storyboardScenes: options.storyboardScenes,
    storyboardDraftScenes,
    consistencyAssets,
    editorContent: options.editorContent,
    updateWorkflowInputData: (payload) => updateWorkflowInputData(options.projectId, payload),
    routerPush: options.routerPush,
    markEditorDirty: options.markEditorDirty,
    buildDefaultSceneImagePrompt: (scene) => buildSceneImageCreativePrompt(buildScenePromptEngineeringContext({
      scene,
      consistencyAssets: consistencyAssets.value,
      consistencyAssetLookups: consistencyAssetLookups.value,
    })),
    success: (message) => ElMessage.success(message),
    error: (message) => ElMessage.error(message),
    info: (message) => ElMessage.info(message),
    warning: (message) => ElMessage.warning(message),
  })

  useStoryboardReferenceAssets({
    selectedKnowledgeContexts,
  })

  const {
    buildSceneGenerationRequest,
    getStoryboardSceneBindingLabels,
    getStoryboardSceneMeta,
    handleGenerateSceneVideo,
  } = useStoryboardSceneGeneration({
    projectId: options.projectId,
    workflow: options.workflow,
    storyboardDraftScenes,
    sceneImageInputs,
    consistencyAssets,
    consistencyAssetLookups,
    sceneVideoGenerating,
    trackedSceneVideoTaskIds,
    ensureStoryboardScenesReady,
    ensureConsistencyAssetsReady: async () => {
      if (!consistencyDirty.value) {
        return true
      }
      return handleSaveConsistencyAssets('一致性资产已保存，继续生成视频')
    },
    isSceneVideoTaskActive,
    buildSceneKnowledgePrompt,
    refreshSceneVideoTaskStatus,
    generateStoryboardSceneVideoApi: generateStoryboardSceneVideo,
    info: (message) => ElMessage.info(message),
    success: (message) => ElMessage.success(message),
    error: (message) => ElMessage.error(message),
  })

  async function syncQueuedKnowledgeContexts() {
    const queuedKnowledgeContexts = consumeWorkflowKnowledgeContexts(options.projectId)
    const mergedQueuedKnowledge = mergeKnowledgeContexts(queuedKnowledgeContexts)
    if (mergedQueuedKnowledge) {
      await persistKnowledgeContexts({ silent: true })
    }
  }

  return {
    activeConsistencyAssets,
    applyQueuedKnowledgeConsistencyAssetImages,
    applyQueuedKnowledgeSceneImages,
    buildSceneGenerationRequest,
    clearSceneVideoPlaybackError,
    consistencyAssetImageGenerating,
    consistencyAssetImageUploading,
    consistencyAssetSelectionOptions,
    consistencyAssetStats,
    consistencyAssetTab,
    consistencyAssetUploadInput,
    consistencyAssets,
    consistencyDirty,
    consistencySaving,
    ensureStoryboardScenesReady,
    formatSceneImageSource,
    getSceneVideoTaskStatus,
    getStoryboardSceneBindingLabels,
    getStoryboardSceneMeta,
    handleAddConsistencyAsset,
    handleAddStoryboardScene,
    handleCancelSceneVideoUploadDialog,
    handleCancelStoryboardSceneEdit,
    handleClearConsistencyAssetImage,
    handleClearSceneImage,
    handleConfirmSceneVideoUploadDialog,
    handleConsistencyAssetFileSelected,
    handleCreatePromptMentionAsset,
    handleDeleteStoryboardScene,
    handleFinishStoryboardSceneEdit,
    handleGenerateConsistencyAssetReference,
    handleGenerateSceneImage,
    handleGenerateSceneVideo,
    handleInsertStoryboardScene,
    handleOpenKnowledgeBaseForConsistencyAsset,
    handleOpenKnowledgeBaseForScene,
    handleRemoveConsistencyAsset,
    handleSaveAndRegenerateStoryboard,
    handleSaveConsistencyAssets,
    handleSaveSceneVideoFeedback,
    handleSaveStoryboardScenes,
    handleSceneFileSelected,
    handleSceneVideoFileSelected,
    handleSceneVideoLoadedData,
    handleSceneVideoPlaybackError,
    handleSelectSceneReference,
    handleToggleStoryboardSceneEdit,
    hoveredStoryboardSceneNumber,
    hydrateKnowledgeContextsFromWorkflow,
    isSceneImageBusy,
    isSceneImageLoading,
    isSceneVideoGenerating,
    isSceneVideoTaskActive,
    markConsistencyDirty,
    markStoryboardDraftDirty,
    mergeKnowledgeContexts,
    persistKnowledgeContexts,
    refreshSceneVideoTaskStatus,
    sceneUploadInput,
    sceneVideoFeedbackDrafts: options.sceneVideoFeedbackDrafts,
    sceneVideoGenerating,
    sceneVideoLoading,
    sceneVideoSaving,
    sceneVideoUploadDialogVisible,
    sceneVideoUploadFeedbackDraft,
    sceneVideoUploadInput,
    selectedScenePreviewOverlayDescription,
    selectedScenePreviewOverlayNote,
    selectedScenePreviewOverlayTitle,
    selectedScenePreviewOverlayVisible,
    selectedStoryboardBoundAssets,
    selectedStoryboardImageUrl,
    selectedStoryboardPromptMentionItems,
    selectedStoryboardSceneDraft,
    selectedStoryboardSceneNumber,
    selectedStoryboardUploadedVideo,
    selectedStoryboardVideoError,
    selectedStoryboardVideoUrl,
    selectedSceneVideoTask,
    selectedSceneVideoTaskDescription,
    selectedSceneVideoTaskFacts,
    selectedSceneVideoTaskStatusLabel,
    selectedSceneVideoTaskTimeline,
    stopAllSceneVideoTaskPolling,
    storyboardDraftDirty,
    storyboardDraftScenes,
    storyboardEditingSceneNumber,
    storyboardGenerationSceneItems,
    storyboardGenerationTab,
    storyboardSaving,
    storyboardVisualActiveSceneNumber,
    storyboardWorkspaceTab,
    syncConsistencyAssetsFromStoryboard,
    syncQueuedKnowledgeContexts,
    syncSceneVideoTaskPolling,
    syncStoryboardDraftScenes,
    triggerConsistencyAssetUpload,
    triggerSceneFileUpload,
    triggerSceneVideoUpload,
  }
}